"use client";

/**
 * FormModal's part in a multi-step CTA: report completion to the runner (fresh submit, or Continue
 * from an earlier submission), show a "Step N saved" toast when the next step opens, and decide
 * when the "Step X of Y" label still applies. Everything is inert without a `sequence`, so a
 * standalone form behaves exactly as before.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import type { FormField } from "@/lib/database/forms";
import { endsFormEarly } from "@/lib/forms/conditional-visibility";
import type { SubmittedFormResponse } from "@/features/forms/_logic/viewer-form";
import {
  endsCombinedSteps,
  extractCombinedAnswers,
  extractLegalName,
  extractStepContact,
  type CtaStepContact,
  type CtaStepSequenceContext,
} from "../_logic/cta-steps";
import type { FormSubmissionPhase } from "./useFormSubmissionState";

interface UseFormStepSequenceParams {
  sequence: CtaStepSequenceContext | undefined;
  submissionPhase: FormSubmissionPhase;
  submissionRecap: SubmittedFormResponse | null;
  formFields: FormField[];
  visibleFields: FormField[];
  formData: Record<string, unknown>;
  emailTemplates: unknown;
  respondentTypeId: number | null;
  showToast: (message: string, type: "success" | "error") => void;
}

interface UseFormStepSequenceReturn {
  /** Handler for the recap's "Continue to next step", or undefined when there is nothing to continue to. */
  onContinueFromRecap: (() => void) | undefined;
  /** Whether to show "Step X of Y": false outside a sequence and once this step ended it. */
  showStepLabel: boolean;
  /** Word for the form's own pagination: "Page" inside a sequence (which owns "Step"), else "Step". */
  paginationLabel: "Page" | "Step";
  /** Suffix for the dialog's accessible name, e.g. ", step 1 of 2", or "" outside a sequence. */
  dialogLabelSuffix: string;
  /** Pass to `useFormSubmitFlow`: an edit save completes the step. Undefined outside a sequence. */
  onEditSaved: ((recap: SubmittedFormResponse) => void) | undefined;
  /** Whether the current answers end the sequence here (e.g. staff), so the form submits itself. */
  endsSequenceNow: boolean;
  /** The contact a combined CTA's form hands to its booking, from the visible answers. */
  buildHandoffContact: () => CtaStepContact;
}

/**
 * Wires a FormModal into a multi-step CTA.
 * @param params - See {@link UseFormStepSequenceParams}
 * @returns See {@link UseFormStepSequenceReturn}
 * @throws Never
 */
export function useFormStepSequence({
  sequence,
  submissionPhase,
  submissionRecap,
  formFields,
  visibleFields,
  formData,
  emailTemplates,
  respondentTypeId,
  showToast,
}: UseFormStepSequenceParams): UseFormStepSequenceReturn {
  // States
  // Report once per mount. The modal remounts for every step, and the runner also ignores repeats,
  // but this keeps the toast from firing twice.
  const hasReportedRef = useRef(false);

  // Derived values
  const onStepComplete = sequence?.onStepComplete;
  const hasNextStep = !!sequence?.hasNextStep;
  const combined = sequence?.combined ?? null;
  /**
   * Whether answers end the sequence after this form: a skip-to-end answer (e.g. an RSVP of "not
   * attending"), or, in a combined CTA, the event's end rule (e.g. event staff skip the hotel).
   */
  const endsSequence = useCallback(
    (answers: Record<string, unknown>) =>
      endsFormEarly(formFields, answers, respondentTypeId) ||
      (!!combined?.endStepsIf && endsCombinedSteps(formFields, answers, combined.endStepsIf, respondentTypeId)),
    [formFields, respondentTypeId, combined]
  );
  /** The contact to carry forward; a combined CTA also carries the answers its hotel step skips. */
  const contactFrom = useCallback(
    (fields: FormField[], answers: Record<string, unknown>, fallbackEmail: string | null): CtaStepContact => {
      const contact = extractStepContact(fields, answers, emailTemplates);
      if (!combined) return { ...contact, email: contact.email ?? fallbackEmail };
      // The booking goes under the legal name when the guest gave one (it must match their ID).
      const legal = extractLegalName(fields, answers, combined);
      return {
        ...contact,
        firstName: legal.firstName ?? contact.firstName,
        lastName: legal.lastName ?? contact.lastName,
        email: contact.email ?? fallbackEmail,
        carried: extractCombinedAnswers(fields, answers, combined),
      };
    },
    [emailTemplates, combined]
  );
  // An earlier submission can still move the sequence on, unless its saved answers end it.
  const canContinueFromRecap = useMemo(
    () => hasNextStep && !!submissionRecap && !endsSequence(submissionRecap.answers),
    [hasNextStep, submissionRecap, endsSequence]
  );
  const endedEarlyOnSubmit = useMemo(
    () => submissionPhase === "submitted" && endsSequence(formData),
    [submissionPhase, endsSequence, formData]
  );
  // Once this step ended the flow (skip-to-end answer, or a recap with nowhere to continue),
  // "Step 1 of 2" would promise a next step that isn't coming.
  const hasEndedSequence =
    endedEarlyOnSubmit || (hasNextStep && submissionPhase === "previously-submitted" && !canContinueFromRecap);
  // Opened from the overview, or in a combined CTA, the dialog's header already shows where the
  // step sits.
  const showStepLabel = !!sequence && sequence.origin === "run" && !combined && !hasEndedSequence;
  const onEditingChange = sequence?.onEditingChange;
  const isEditing = submissionPhase === "idle" || submissionPhase === "editing";
  const dialogLabelSuffix = sequence ? `, step ${sequence.stepNumber} of ${sequence.stepCount}` : "";

  // Only a combined run asks on every change (its last button flips between Next and Submit).
  const endsSequenceNow = useMemo(
    () => !!sequence?.deferSubmit && endsSequence(formData),
    [sequence?.deferSubmit, endsSequence, formData]
  );

  // Handlers
  const buildHandoffContact = useCallback((): CtaStepContact => {
    const visibleAnswers = Object.fromEntries(visibleFields.map((field) => [field.label, formData[field.label]]));
    return contactFrom(visibleFields, visibleAnswers, null);
  }, [visibleFields, formData, contactFrom]);

  const handleContinueFromRecap = useCallback(() => {
    if (!onStepComplete || !submissionRecap || hasReportedRef.current) return;
    hasReportedRef.current = true;
    onStepComplete({
      contact: contactFrom(formFields, submissionRecap.answers, submissionRecap.email),
      endedEarly: false,
    });
  }, [onStepComplete, submissionRecap, formFields, contactFrom]);

  /** An edit to an earlier submission saved: that completes the step too (e.g. back to the overview). */
  const handleEditSaved = useCallback(
    (recap: SubmittedFormResponse) => {
      if (!onStepComplete || hasReportedRef.current) return;
      hasReportedRef.current = true;
      onStepComplete({
        contact: contactFrom(formFields, recap.answers, recap.email),
        endedEarly: endsSequence(recap.answers),
      });
    },
    [onStepComplete, formFields, contactFrom, endsSequence]
  );

  // Effects
  // Tell the dialog whether a form is open for input, so leaving can ask about unsaved changes.
  useEffect(() => {
    onEditingChange?.(isEditing);
  }, [onEditingChange, isEditing]);

  // Report a fresh submission once. Every submit path (plain, paid, free tier) ends in the
  // "submitted" phase, so this is the single place completion is detected. Only visible answers
  // count, so a value left in a skipped field can't be carried forward.
  useEffect(() => {
    if (!onStepComplete || submissionPhase !== "submitted" || hasReportedRef.current) return;
    hasReportedRef.current = true;
    const visibleAnswers = Object.fromEntries(visibleFields.map((field) => [field.label, formData[field.label]]));
    onStepComplete({
      contact: contactFrom(visibleFields, visibleAnswers, null),
      endedEarly: endedEarlyOnSubmit,
    });
    // The next step replaces this modal (and its thank-you screen) straight away, so confirm the
    // save here. The toast host lives above the modals, so it outlives this one. A combined CTA
    // reads as one form, so it moves on without one.
    if (hasNextStep && !endedEarlyOnSubmit && !combined && sequence) {
      showToast(`Step ${sequence.stepNumber} saved`, "success");
    }
  }, [
    onStepComplete,
    submissionPhase,
    visibleFields,
    formData,
    contactFrom,
    combined,
    endedEarlyOnSubmit,
    hasNextStep,
    sequence,
    showToast,
  ]);

  return {
    onContinueFromRecap: canContinueFromRecap ? handleContinueFromRecap : undefined,
    showStepLabel,
    paginationLabel: sequence ? "Page" : "Step",
    dialogLabelSuffix,
    onEditSaved: sequence ? handleEditSaved : undefined,
    endsSequenceNow,
    buildHandoffContact,
  };
}
